import debounce from 'just-debounce';

/**
 * @class MainCart
 * @classdesc The cart section is used to handle the cart page functionality.
 *
 * @extends Core.SectionComponent
 */
export default class MainCart extends Core.SectionComponent {
  constructor(el, context) {
    super(el, context);

    this.headerCartCount = el.querySelector('[data-header-cart-count]');
    this.cartPage = el.querySelector('[data-cart-page]');
    this.cartErrors = el.querySelector('[data-cart-errors]');
    this.cartForm = el.querySelector('[data-cart-form]');
    this.cartContents = el.querySelector('[data-cart-contents]');
    this.cartItems = el.querySelector('[data-cart-items]');
    this.cartItemErrors = el.querySelectorAll('[data-cart-item-error]');
    this.cartTotal = el.querySelector('[data-cart-total]');


    if (!this.cartForm) return;

    this.cartItemCount = parseInt(this.cartForm.dataset.cartCount, 10);
    this.cartId = this.cartContents.dataset.id;
  }

  /**
   * On mount, we create the event listeners for the cart section.
   */
  onMount() {
    if (!this.cartForm) return;
    this._createListeners();
  }

  /**
   * Creates event listeners used to detect changes to line item quantity changes
   *
   * @private
   */
  _createListeners() {
    const cartRows = this.cartForm.querySelectorAll('[data-cart-row]');

    // Bind quantity selectors
    cartRows.forEach(row => {
      const rowId = row.id;
      const rowKey = row.dataset.cartItemKey;
      const quantitySelector = this.query('quantity-selector', rowKey);
      const buttonRemoveItem = row.querySelector('[data-remove]');


      if (quantitySelector) {
        this.events.register(quantitySelector.el, 'quantity:update', e => {
          if (e.detail.quantity === 0) {
            this._removeRow({ id: rowId, key: rowKey, row: row, event: e });
          } else {
            this._updateCart(rowId, rowKey, e);
          }
        });
      }

      if (buttonRemoveItem) {
        this.events.register(buttonRemoveItem, 'click', e => {
          e.preventDefault();
          this._removeRow({ id: rowId, key: rowKey, row: row, event: 0 });
        });
      }
    });

    this.events.register(document, 'cart:update', e => {
      if (this.headerCartCount) {
        this.headerCartCount.innerHTML = e.detail.count;
      }
    });

    const cartNotes = this.query('input-textarea');

    if (cartNotes) {
      const notesTextArea = cartNotes.el.querySelector('textarea');
      this.events.register(notesTextArea, 'change', debounce(e => this._updateNote(e), 500));
    }
  }

  /**
   * Updates the cart with the new quantity and refreshes the innerHTML content.
   *
   * @param {number} id - The id of the line item to update.
   * @param {number} rowKey- The key of the line item to update.
   * @param {object} e - The event object triggered.
   * @private
   */
  _updateCart(rowId, rowKey, e) {
    let quantity;
    if (e.detail !== undefined) {
      ({ quantity } = e.detail);
    } else {
      quantity = 0;
    }

    const body = JSON.stringify({
      line: rowId,
      quantity,
      sections: this.cartPage.dataset.id,
      sections_url: window.location.pathname,
    });

    fetch(window.PXUTheme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      ...{ body },
    })
      .then(response => response.text())
      .then(state => {
        const parsedState = JSON.parse(state);
        const newDom = new DOMParser().parseFromString(parsedState.sections[this.sectionId], 'text/html');

        // Update the cart page content
        this._clearErrors();

        if (parsedState.item_count > 0) {
          if (this.cartItems) {
            this.cartItems.innerHTML = newDom.querySelector('[data-cart-items]').innerHTML;
          }

          if (this.cartTotal) {
            this.cartTotal.innerHTML = newDom.querySelector('[data-cart-total]').innerHTML;
          }

          const recommendationsSlider = this.query('product-cross-sell-slider');

          if (recommendationsSlider) {
            const productId = parsedState.items[0].product_id;
            const sectionId = this.sectionId;

            recommendationsSlider.fetchRecommendations(sectionId, productId);
          }
        } else if (this.cartPage) {
          this.cartPage.innerHTML = newDom.querySelector('[data-cart-page]').innerHTML;
        }


        // Return data for inventory handling
        return { key: rowKey, count: parsedState.item_count };
      }).then(data => {
        const targetSelector = this.query('quantity-selector', rowKey);
        const idTarget = this.cartForm.querySelector(`[data-cart-row="${rowId}"]`);

        if (targetSelector && e.detail !== undefined) {
          // Set focus back on the original element
          targetSelector.focus(e.detail.actionName);
        } else if (idTarget) {
          // If original key is gone from the dom lets focus on the new one in it's row
          const targetInput = idTarget.querySelector('[data-quantity-input]');
          targetInput.focus();
        } else {
          // If the original key and row is gone from the dom lets focus on the next one above it
          for (let i = rowId; i > 0; i--) {
            const target = this.cartForm.querySelector(`[data-cart-row="${i}"]`);

            if (target) {
              const targetInput = target.querySelector('[data-quantity-input]');
              targetInput.focus();

              break;
            }
          }
        }

        this._handleInventory(data.key, data.count);
        this._removeListeners();
        this._createListeners();
      })
      .catch(error => {
        this._handleError(error);
      });
  }

  /**
   * Handles error messaging for cart quantity changes.
   *
   * @param {number} rowKey - The id of the line item to update.
   * @param {number} itemCount - The current quantity of items in the cart
   * @private
   */
  _handleInventory(rowKey, itemCount) {
    if (this.cartItemCount === itemCount) {
      const targetSelector = this.query('quantity-selector', rowKey);
      const inputValue = targetSelector.getValue();

      document.querySelector(`[data-cart-item-error="${rowKey}"]`).innerHTML = this.data.locales.quantityError.replace('{{ quantity }}', inputValue);
    }

    this.cartItemCount = itemCount;

    const updateEvent = new CustomEvent('cart:update', { detail: { count: this.cartItemCount }, bubbles: true });
    this.el.dispatchEvent(updateEvent);
  }

  /**
   * Handles general error messaging.
   *
   * @private
   */
  _handleError(error) {
    this.cartErrors.style.display = 'block';
    this.cartErrors.innerHTML = this.data.locales.error;
    this._removeListeners();
    this._createListeners();

    console.error(error);
  }

  /**
   * Clear error messaging from the cart section.
   *
   * @private
   */
  _clearErrors() {
    this.cartErrors.style.display = 'none';
    this.cartErrors.innerHTML = '';
    this.cartItemErrors.forEach(error => {
      error.innerHTML = '';
    });
  }

  /**
   * Animates the removal of items from the cart
   *
   * @param {object} data - The data object containing the id, key, and row of the item to remove.
   * @private
   */
  _removeRow(data) {
    const { id, key, row, event } = data;
    const animateRemoval = Core.Animations.transition({ el: row, state: 'active' });
    row.style.setProperty('--open-height', `${row.scrollHeight}px`);
    animateRemoval.animateTo('fade')
      .then(() => {
        animateRemoval.animateTo('remove')
          .then(() => {
            row.remove();
            this._updateCart(id, key, event);
          });
      });
  }

  _updateNote(e) {
    const body = JSON.stringify({ note: e.target.value});
    fetch(window.PXUTheme.routes.cart_update_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      ...{ body },
    });
  }

  /**
   * Used to unregister all the event handles initialized on the cart section
   *
   * @private
   */
  _removeListeners() {
    this.events.unregisterAll();
  }
}
